import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { api, errText } from "@/lib/api";
import {
  fmtDate, fmtMoney, fmtNumber, toInputDate, DELAY_REASONS,
} from "@/lib/format";
import { LdBadge, StageBadge, Mono } from "@/components/Badges";
import StageTracker from "@/components/StageTracker";
import { F, fieldsOf, readFieldsOf, fieldDefIn, Field, payloadFrom } from "@/components/StepFields";
import MailDialog from "@/components/MailDialog";
import { rememberPo } from "@/pages/PoHistory";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { useCustomer } from "@/lib/customer";
import { toast } from "sonner";

const IDENTITY = [
  ["poNumber", "PO number"], ["poDate", "PO date", "date"], ["branchName", "Branch"],
  ["branchCode", "Branch code"], ["spocName", "SPOC"], ["spocContact", "SPOC contact"],
  ["itemType", "Item"], ["qty", "Qty", "number"], ["rate", "Rate", "money"], ["owner", "Owner"],
];

function valueOf(line, key) {
  return key in line ? line[key] : (line.custom || {})[key];
}

function show(value, type) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined || value === "") return "—";
  if (type === "date") return fmtDate(value);
  if (type === "money") return fmtMoney(value);
  if (type === "number") return fmtNumber(value);
  return String(value);
}



export default function PoView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const fromTask = params.get("task");
  const taskStage = Number(params.get("stage")) || null;
  const navigate = useNavigate();
  const { isManager } = useAuth();
  const { customerId, pick } = useCustomer();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState("");
  const [values, setValues] = useState({});
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [blocker, setBlocker] = useState("");
  const [chaseDate, setChaseDate] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [blockOpen, setBlockOpen] = useState(false);
  const [editStage, setEditStage] = useState(null);
  const [openStages, setOpenStages] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [dispatchScope, setDispatchScope] = useState("full");
  const [remark, setRemark] = useState("");
  const [saved, setSaved] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/po-detail/${id}`);
      setData(data);
      setMissing("");
      setValues({});
    } catch (e) {
      if (e?.response?.status === 404 || e?.response?.status === 403) {
        setMissing("This PO is not on the app any more, or it belongs to a customer you cannot see.");
        return;
      }
      toast.error(errText(e));
    }
  }, [id]);

  useEffect(() => {
    load();
    api.get("/meta").then(({ data }) => setPeople(data.people)).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!fromTask || !data) return;
    document.getElementById("po-step")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [fromTask, data]);

  useEffect(() => {
    if (!data) return;
    const g = data.po;
    rememberPo({
      key: g.key,
      lineId: g.lead.lineId,
      poNumber: g.poNumber,
      poDate: g.poDate,
      branchName: g.branchName,
      branchCode: g.branchCode,
      lineCount: g.counts.total,
      stageLabel: g.lead.stageLabel,
      ldColour: g.ldColour,
      ldStatus: g.ldStatus,
      value: g.value,
      isClosed: g.counts.closed === g.counts.total,
    });
  }, [data]);

  if (missing) return <p className="text-sm text-muted-foreground" data-testid="po-missing">{missing}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const po = data.po;
  // the stages, the boxes and the wording all come from this PO's own customer, never the picker
  const stages = data.workflow?.stages || [];
  const stagesReady = !!data.workflow;
  const lead = data.poStepLine;
  const single = data.lines.length === 1;
  const step = data.poStep;
  // opened from a follow-up: work on that reminder's line, at that line's own stage
  const focusLine = fromTask ? data.lines.find((l) => l.id === data.anchorId) : null;
  const activeLine = focusLine || lead;
  const task = fromTask ? (data.tasks || []).find((t) => t.id === fromTask) : null;
  // a reminder opens the stage it is about, however far the line has otherwise moved on
  const activeStage = editStage ?? taskStage ?? activeLine.derived.stage;
  const activeStep = (po.steps || []).find((s) => s.stage === activeStage) || {
    label: activeLine.derived.stageLabel,
    action: activeLine.derived.nextAction,
  };
  const isRevisit = activeStage !== step.stage || !!focusLine;
  const stepFields = fieldsOf(stages, activeStage);
  const stepLineCount = data.lines.length;
  // every customer's flow has its own stage ids, so the full / in-part choice follows the
  // stage's own completing field instead of one hard-coded stage
  const activeStageDef = (stages || []).find((s) => Number(s.id) === Number(activeStage));
  const partialKey = activeStageDef?.completeField ? `${activeStageDef.completeField}Partial` : null;

  const openStep = (stage) => {
    setEditStage(stage);
    setValues({});
    setTimeout(
      () => document.getElementById("po-step")?.scrollIntoView({ behavior: "smooth", block: "center" }),
      50
    );
  };

  const setValue = (name, value) => setValues((v) => ({ ...v, [name]: value }));
  const current = (name) => {
    if (name in values) return values[name];
    const kind = fieldDefIn(stages, name)?.type || F[name]?.type;
    const raw = name in activeLine ? activeLine[name] : (activeLine.custom || {})[name];
    if (kind === "date") return toInputDate(raw);
    if (kind === "check") return !!raw;
    return raw ?? "";
  };

  // every stage is recorded for the whole PO; the items are data, not separate things to chase
  const targetIds = () => data.lines.map((l) => l.id);
  const scopeChoice = partialKey && current(partialKey) ? "part" : "full";

  const save = async () => {
    // whatever is in the boxes is what gets saved, typed now or read back from an earlier visit
    const shown = {};
    stepFields.forEach((def) => {
      shown[def.key] = current(def.key);
    });
    if (partialKey && partialKey in values) shown[partialKey] = values[partialKey];
    const payload = payloadFrom(shown);
    const anything = Object.values(payload).some((v) => v !== null && v !== false && v !== "");
    if (!anything) {
      toast.info("Fill in at least one box on this stage first");
      return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.patch(`/polines/${activeLine.id}`, {
        values: payload,
        lineIds: targetIds(),
      });
      const refused = res.refused || [];
      if (refused.length) {
        // nothing is confirmed unless it was written: name the box and why it was refused
        const names = refused.map((k) => fieldDefIn(stages, k)?.label || k).join(", ");
        toast.error(`Not saved: ${names} — this box is not part of this PO's flow any more.`);
        await load();
        return;
      }
      toast.success(
        res.linesUpdated > 1
          ? `Recorded on ${res.linesUpdated} lines of this PO`
          : "Recorded on this line"
      );
      setEditStage(null);
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const saveBlocked = async () => {
    if (!blocker.trim()) {
      toast.error("Say what is blocking this PO");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/polines/${lead.id}/blocked`, {
        blocker,
        nextFollowUpDate: chaseDate || null,
        delayReason: blockReason || null,
      });
      toast.success("Blocker recorded and the chase is scheduled");
      setBlocker("");
      setChaseDate("");
      setBlockReason("");
      setBlockOpen(false);
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const updateAllLines = async (name, value) => {
    try {
      const { data: res } = await api.patch(`/polines/${lead.id}`, {
        values: { [name]: value === "" ? null : value },
        lineIds: data.lines.map((l) => l.id),
      });
      const label = fieldDefIn(stages, name)?.label || F[name]?.label || "Detail";
      if ((res.refused || []).length) {
        toast.error(`Not saved: ${label} — this box is not part of this PO's flow any more.`);
        return;
      }
      toast.success(`${label} saved`);
      setSaved(label);
      await load();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const addRemark = async () => {
    if (!remark.trim()) {
      toast.error("Type the remark first");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/po/${lead.id}/remark`, { text: remark });
      setRemark("");
      toast.success("Remark added to the note");
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="po-view-page">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="po-back-button">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <MailDialog mails={data.mails || []} />
        {isManager && (
          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="ml-auto text-red-600" data-testid="po-delete-button">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete this PO
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete this whole PO?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <p>
                  All <Mono>{data.lines.length}</Mono> line(s) of <Mono>{po.poNumber}</Mono> ({po.branchName}) will be
                  hidden from every screen. Nothing is destroyed — ask and it can be brought back.
                </p>
                <div>
                  <Label className="field-label">Type DELETE to confirm</Label>
                  <Input
                    value={deleteText}
                    onChange={(e) => setDeleteText(e.target.value)}
                    data-testid="po-delete-confirm-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="destructive"
                  disabled={deleteText !== "DELETE" || busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const { data: res } = await api.delete(`/po/${data.anchorId}`);
                      toast.success(`PO deleted — ${res.deleted} line(s) hidden`);
                      navigate("/pos");
                    } catch (e) {
                      toast.error(errText(e));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  data-testid="po-delete-confirm-button"
                >
                  Delete the PO
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* PO header */}
      <div className="panel p-3 sm:p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {po.branchName || "Branch not named"}
            </h1>
            <p className="mt-1 text-xs text-muted-foreground">
              <Mono>{po.poNumber}</Mono> · {po.branchCode || "—"} · {po.city || ""} {po.state || ""} ·{" "}
              <Mono>{po.counts.total}</Mono> item{po.counts.total === 1 ? "" : "s"} · {fmtMoney(po.value)}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              SPOC {po.spocName || "—"} · {po.spocContact || "—"} · owner {po.owners.join(", ")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StageBadge label={po.lead.stageLabel} testId="po-stage-badge" />
            <LdBadge colour={po.ldColour} text={po.ldStatus} testId="po-ld-badge" />
            {(po.partialMarks || []).map((mark) => (
              <span
                key={mark}
                className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground"
                data-testid={`partial-mark-${mark.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {mark} in part
              </span>
            ))}
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
          {[
            ["PO date", fmtDate(po.poDate)],
            ["Delivery due", fmtDate(po.deliveryDue)],
            ["Completion due", fmtDate(po.completionDue)],
            ["Ageing", `${po.ageing}d`],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="text-muted-foreground">{label}</div>
              <Mono>{value}</Mono>
            </div>
          ))}
        </div>

        {!single && (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="po-progress">
            <Mono>{po.counts.total}</Mono> items on this PO
          </p>
        )}

        {(po.partialMarks || []).length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground" data-testid="po-dispatch-state">
            {po.partialMarks.join(", ")} went out in part.
          </p>
        )}
      </div>

      {/* where this PO stands */}
      <div className="panel p-3 sm:p-4" data-testid="po-tracker-panel">
        <div className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">Where this PO stands</div>
        <StageTracker steps={po.steps || []} testId="po-tracker" onStageClick={(s) => openStep(s.stage)} />
        <p className="mt-3 text-xs text-muted-foreground">
          Tap any stage to open it and record or correct what is against it.
        </p>
      </div>

      {/* the PO step */}
      <div id="po-step" className="panel p-3 sm:p-4" data-testid="step-panel">
        {fromTask && (
          <div
            className="mb-3 rounded-md border border-primary/40 bg-primary/5 p-2 text-xs"
            data-testid="from-followup-note"
          >
            Opened from a follow-up on the <strong>{activeLine.itemType || "this"}</strong> line
            {task ? <> — {task.why}</> : "."}
            {taskStage && taskStage !== activeLine.derived.stage ? (
              <> The line itself has moved on to <strong>{activeLine.derived.stageLabel}</strong>.</>
            ) : null}
            {" "}Record it here and it drops off the follow-up list.
          </div>
        )}
        <div className="mb-1 flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
          {isRevisit ? "Recording this stage" : "Current step"}
          {isRevisit && (
            <button
              onClick={() => {
                setEditStage(activeLine.derived.stage);
                setValues({});
              }}
              className="normal-case text-primary hover:underline"
              data-testid="back-to-current-step"
            >
              Back to the current step ({step.stageLabel})
            </button>
          )}
        </div>
        <h2 className="text-lg font-medium tracking-tight" data-testid="step-stage-label">
          {activeStep.label || activeStep.stageLabel}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="step-next-action">
          {activeStep.action || activeStep.nextAction}
        </p>
        {focusLine ? (
          <p className="mt-1 text-xs text-muted-foreground" data-testid="step-scope-note">
            This is recorded for the whole PO.
          </p>
        ) : !single && (
          <p className="mt-1 text-xs text-muted-foreground" data-testid="step-scope-note">
            This is recorded for the whole PO — all <Mono>{data.lines.length}</Mono> items at once.
          </p>
        )}

        {!stagesReady ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="step-fields-loading">
            Fetching this customer's flow…
          </p>
        ) : stepFields.length > 0 ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {stepFields.map((def) => (
              <Field key={def.key} name={def.key} def={def} value={current(def.key)} onChange={setValue} />
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="step-fields-empty">
            There is nothing to record at this stage.
          </p>
        )}

        {!single && partialKey && (
          <div className="mt-4 rounded-md border border-border p-3" data-testid="dispatch-scope-picker">
            <div className="text-xs font-medium">Is this stage done for the whole PO?</div>
            <p className="mt-1 text-xs text-muted-foreground">
              In part moves the PO on and leaves an "in part" mark on it for the record.
            </p>
            <div className="mt-2 flex flex-wrap gap-4 text-sm">
              {[["full", "For the whole PO"], ["part", "In part"]].map(([value, label]) => (
                <label key={value} className="flex items-center gap-2" htmlFor={`dispatch-scope-${value}`}>
                  <input
                    id={`dispatch-scope-${value}`}
                    type="radio"
                    name="dispatch-scope"
                    checked={scopeChoice === value}
                    onChange={() => {
                      setValue(partialKey, value === "part");
                    }}
                    data-testid={`dispatch-scope-${value}`}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={save} disabled={busy || !stagesReady || stepFields.length === 0} data-testid="record-and-move-on-button">
            {isRevisit ? "Save this stage" : "Record and move on"}
          </Button>
          <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" disabled={busy} data-testid="cannot-proceed-button">
                Can't proceed yet
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>What is blocking this PO?</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="field-label">What is blocking it</Label>
                  <Textarea value={blocker} onChange={(e) => setBlocker(e.target.value)} rows={3} data-testid="blocker-text-input" />
                </div>
                <div>
                  <Label className="field-label">Chase again on</Label>
                  <Input type="date" value={chaseDate} onChange={(e) => setChaseDate(e.target.value)} className="font-mono" data-testid="blocker-chase-date-input" />
                </div>
                <div>
                  <Label className="field-label">Delay reason</Label>
                  <select
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="blocker-reason-select"
                  >
                    <option value="">Not set</option>
                    {DELAY_REASONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveBlocked} disabled={busy} data-testid="blocker-save-button">
                  Record the blocker
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* assignment and delay */}
      <div className="panel p-3 sm:p-4">
        <h3 className="text-sm font-medium">PO details, assignment and delay</h3>
        <p className="mt-1 text-xs text-muted-foreground">These apply to the whole PO.</p>
        {saved && (
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300" data-testid="details-saved-note">
            Saved — {saved}
          </p>
        )}
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="field-label">Owner{!isManager && " (manager only)"}</Label>            <select
              value={lead.owner || ""}
              disabled={!isManager}
              onChange={(e) => updateAllLines("owner", e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-60"
              data-testid="po-owner-select"
            >
              <option value="">Unassigned</option>
              {people.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          {["soNumber", "poProcessedDate", "delayReason", "nextFollowUpDate", "followUpCategory", "kanexRemark"].map(
            (name) => (
              <Field
                key={name}
                name={name}
                value={current(name)}
                onChange={(n, v) => {
                  setValue(n, v);
                  updateAllLines(n, v);
                }}
                prefix="assign"
              />
            )
          )}
        </div>

        <div className="mt-4 border-t border-border pt-4" data-testid="remark-note">
          <Label className="field-label">Remark on this PO</Label>
          <Textarea
            rows={2}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="What happened, in a line. It joins the note below and is never overwritten."
            data-testid="remark-input"
          />
          <Button size="sm" className="mt-2" onClick={addRemark} disabled={busy} data-testid="remark-save-button">
            Add to the note
          </Button>
          <ul className="mt-3 space-y-2 text-xs" data-testid="remark-list">
            {(data.remarks || []).length === 0 ? (
              <li className="text-muted-foreground">No remark written yet.</li>
            ) : (
              (data.remarks || []).map((entry, i) => (
                <li key={i} className="border-l-2 border-border pl-3" data-testid={`remark-entry-${i}`}>
                  <div className="text-muted-foreground">
                    <Mono>{fmtDate(entry.timestamp)}</Mono> · {entry.user}
                  </div>
                  <div>{entry.text}</div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      {/* the whole story, stage by stage */}
      <div className="panel p-3 sm:p-4" data-testid="stage-record-panel">
        <h3 className="text-sm font-medium">The whole story, stage by stage</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Scroll the PO end to end. Open any stage to see what is recorded, or edit it.
        </p>
        <div className="mt-3 space-y-2">
          {(po.steps || []).map((s) => {
            const fields = readFieldsOf(stages, s.stage);
            const isOpen = openStages[s.stage] ?? s.state === "current";
            return (
              <div key={s.stage} className="rounded-md border border-border" data-testid={`stage-block-${s.stage}`}>
                <button
                  onClick={() => setOpenStages((o) => ({ ...o, [s.stage]: !isOpen }))}
                  className="flex w-full items-center justify-between gap-2 p-3 text-left"
                  data-testid={`stage-block-toggle-${s.stage}`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {s.state === "done" ? (
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                        <Check className="h-3 w-3" />
                      </span>
                    ) : (
                      <span
                        className={`h-5 w-5 shrink-0 rounded-full border-2 ${
                          s.state === "current" ? "border-emerald-600" : "border-border"
                        }`}
                      />
                    )}
                    <span className={`truncate text-sm ${s.state === "current" ? "font-medium" : ""}`}>{s.label}</span>
                    {s.date && <Mono className="text-xs text-muted-foreground">{fmtDate(s.date)}</Mono>}
                  </span>
                  {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="border-t border-border p-3">
                    <p className="text-xs text-muted-foreground">{s.action}</p>
                    {fields.length === 0 ? (
                      <p className="mt-2 text-xs text-muted-foreground">There is nothing to record at this stage.</p>
                    ) : (
                      <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
                        {fields.map(({ key, label, type }) => (
                          <div key={key}>
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd data-testid={`stage-${s.stage}-${key}`}>
                              {single ? (
                                <span className={type ? "font-mono tabular-nums" : ""}>
                                  {show(valueOf(data.lines[0], key), type)}
                                </span>
                              ) : (
                                data.lines.map((l) => (
                                  <span key={l.id} className="mr-2 inline-block">
                                    <span className="text-muted-foreground">{l.itemType}:</span>{" "}
                                    <span className={type ? "font-mono tabular-nums" : ""}>
                                      {show(valueOf(l, key), type)}
                                    </span>
                                  </span>
                                ))
                              )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {fields.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => openStep(s.stage)}
                        data-testid={`stage-edit-${s.stage}`}
                      >
                        Record or edit this stage
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* the lines */}
      <div className="panel p-3 sm:p-4" data-testid="lines-panel">
        <h3 className="text-sm font-medium">
          The item{po.counts.total === 1 ? "" : "s"} on this PO
        </h3>
        <div className="mt-3 space-y-2">
          {data.lines.map((line) => {
            const isOpen = !!expanded[line.id];
            return (
              <div key={line.id} className="rounded-md border border-border" data-testid={`line-row-${line.id}`}>
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [line.id]: !isOpen }))}
                  className="flex w-full items-start justify-between gap-2 p-3 text-left"
                  data-testid={`line-expand-${line.id}`}
                >
                  <span className="min-w-0">
                    <span className="text-sm font-medium">{line.itemType || "—"}</span>
                    {line.qty ? <Mono className="text-sm"> × {fmtNumber(line.qty)}</Mono> : null}
                    <span className="ml-2 text-xs text-muted-foreground">{fmtMoney(line.derived.lineValue)}</span>
                    {line.product && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">{line.product}</span>
                    )}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <StageBadge label={line.derived.stageLabel} testId={`line-stage-${line.id}`} />
                    <LdBadge colour={line.derived.ldColour} text={line.derived.ldStatus} />
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-border p-3">
                    <div className="mb-3">
                      <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                        This PO
                      </div>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
                        {IDENTITY.map(([key, label, type]) => (
                          <div key={key}>
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd className={type ? "font-mono tabular-nums" : ""} data-testid={`read-${key}-${line.id}`}>
                              {show(line[key], type)}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                    {(po.steps || []).map((s) => {
                      const fields = readFieldsOf(stages, s.stage);
                      if (fields.length === 0) return null;
                      return (
                        <div key={s.stage} className="mb-3" data-testid={`read-stage-${s.stage}-${line.id}`}>
                          <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                            {s.label}
                          </div>
                          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
                            {fields.map(({ key, label, type }) => (
                              <div key={key}>
                                <dt className="text-muted-foreground">{label}</dt>
                                <dd className={type ? "font-mono tabular-nums" : ""} data-testid={`read-${key}-${line.id}`}>
                                  {show(valueOf(line, key), type)}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </div>
                      );
                    })}
                    {isManager && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm("Delete this line? It is hidden, not destroyed.")) return;
                          await api.delete(`/polines/${line.id}`).catch((e) => toast.error(errText(e)));
                          toast.success("Line deleted");
                          if (data.lines.length === 1) navigate("/pos");
                          else await load();
                        }}
                        data-testid={`line-delete-${line.id}`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete this line
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* activity */}
      <div className="panel p-3 sm:p-4">
        <h3 className="text-sm font-medium">Activity log</h3>
        <p className="text-xs text-muted-foreground">
          Every line of this PO, newest first. Append-only — nobody can edit it.
        </p>
        <ul className="mt-3 space-y-2 text-xs" data-testid="activity-log">
          {data.activity.map((entry, i) => (
            <li key={i} className="border-l-2 border-border pl-3">
              <div className="text-muted-foreground">
                <Mono>{fmtDate(entry.timestamp)}</Mono> · {entry.user}
                {!single && entry.itemType ? ` · ${entry.itemType}` : ""}
              </div>
              <div>{entry.text}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
